// بافت حالت آفلاین اپ کارکنان (ADR-011، ARD-offline؛ AP-OFF-01): تشخیص اتصال (رویداد مرورگر + Heartbeat هر ۱۰ ثانیه)، Snapshot رمزشده شعبه
// فعال (دریافت در شروع و هر ۱۵ دقیقه برخط؛ عمر ۴۸ ساعت)، Client Outbox و همگام‌سازی خودکار پس از اتصال، ادامه نشست بدون اتصال تا ۸ ساعت و
// پاک‌سازی کامل گاوصندوق با خروج یا جابه‌جایی مستأجر.
import {
  isApiError,
  unwrap,
  type BilOfflineCheckoutInput,
  type BilOfflinePatientInput,
  type BilSyncOperationsRequest,
} from '@phoenix/api-client';
import { useAccess, useAuth, useTenantSession } from '@phoenix/auth';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { deviceId as loadDeviceId, nextSequence, provisionalNumber } from './ids';
import { checkInOperation, checkoutOperation, enqueue, registerOperation, syncOutbox, type SyncSummary, type SyncTransport } from './outbox';
import { offlineStoreFactory, type OfflineStore } from './store';
import { OfflineVault, type OutboxDisplay, type OutboxEntry, type StoredSnapshot } from './vault';

export type ConnectionStatus = 'online' | 'offline';

/** Permissionهای میز آفلاین (هر یک کافی است). */
export const OFFLINE_DESK_PERMISSIONS = ['sch.appointment.checkin', 'pat.patient.create', 'bil.payment.receive'] as const;

export const HEARTBEAT_INTERVAL_MS = 10_000;
export const SNAPSHOT_REFRESH_MS = 15 * 60_000;
export const DEFAULT_MAX_OFFLINE_HOURS = 8;

export interface OfflineContextValue {
  status: ConnectionStatus;
  /** لحظه قطع اتصال (ms). */
  offlineSince: number | null;
  /** بیش از ۸ ساعت بدون اتصال: ثبت عملیات تازه متوقف تا اتصال و ورود دوباره (ARD-offline §۴). */
  sessionExpired: boolean;
  ready: boolean;
  deviceId: string | null;
  snapshot: StoredSnapshot | null;
  entries: readonly OutboxEntry[];
  pendingCount: number;
  conflictCount: number;
  syncing: boolean;
  lastSync: SyncSummary | null;
  error: unknown;
  refreshSnapshot: () => Promise<void>;
  syncNow: () => Promise<SyncSummary | null>;
  checkIn: (appointmentId: string, display?: OutboxDisplay) => Promise<OutboxEntry>;
  register: (patient: BilOfflinePatientInput, display?: OutboxDisplay) => Promise<OutboxEntry>;
  checkout: (input: Omit<BilOfflineCheckoutInput, 'sequence'>, display?: OutboxDisplay) => Promise<OutboxEntry>;
}

const OfflineContext = createContext<OfflineContextValue | null>(null);

/** پیش‌فرض بیرون از Provider (تست صفحات): همیشه برخط. */
const ONLINE_DEFAULT = { status: 'online' as ConnectionStatus, pendingCount: 0 };

export function useOffline(): OfflineContextValue {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOffline must be used inside <OfflineProvider>.');
  }
  return context;
}

/** وضعیت اتصال برای صفحات دیگر (مثلاً غیرفعال کردن استرداد آفلاین — BR-BIL-055)؛ بیرون از Provider برخط. */
export function useConnection(): { status: ConnectionStatus; pendingCount: number } {
  return useContext(OfflineContext) ?? ONLINE_DEFAULT;
}

export interface OfflineProviderProps {
  children: ReactNode;
  /** ذخیره‌گاه (تست: حافظه‌ای). */
  store?: OfflineStore;
  /** ساعت (تست). */
  now?: () => number;
  heartbeatMs?: number;
}

export function OfflineProvider({ children, store: customStore, now = Date.now, heartbeatMs = HEARTBEAT_INTERVAL_MS }: OfflineProviderProps) {
  const auth = useAuth();
  const access = useAccess();
  const { tenantApi, session, onReset } = useTenantSession();
  const [store] = useState(() => customStore ?? offlineStoreFactory.create());
  const [vault, setVault] = useState<OfflineVault | null>(null);
  const [device, setDevice] = useState<string | null>(null);
  const [browserOnline, setBrowserOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine !== false));
  const [serverReachable, setServerReachable] = useState(true);
  const [offlineSince, setOfflineSince] = useState<number | null>(null);
  const [snapshot, setSnapshot] = useState<StoredSnapshot | null>(null);
  const [entries, setEntries] = useState<readonly OutboxEntry[]>([]);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState<SyncSummary | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [clock, setClock] = useState(() => now());
  const syncingRef = useRef<Promise<SyncSummary | null> | null>(null);

  const tenantId = session?.tenantId ?? null;
  const branchId = session?.activeBranchId ?? null;
  const userId = auth.user?.subject ?? null;
  const deskAllowed = OFFLINE_DESK_PERMISSIONS.some((permission) => access.can(permission));
  const status: ConnectionStatus = browserOnline && serverReachable ? 'online' : 'offline';

  // گاوصندوق دامنه (مستأجر، کاربر)؛ دامنه دیگر ← پاک‌سازی (ARD-offline §۲).
  useEffect(() => {
    // بدون Permission میز آفلاین هیچ گاوصندوق، Heartbeat یا Snapshot ساخته نمی‌شود.
    if (!tenantId || !userId || !deskAllowed) {
      setVault(null);
      return undefined;
    }
    let cancelled = false;
    void (async () => {
      try {
        const opened = await OfflineVault.open(store, { tenantId, userId }, now());
        const code = await loadDeviceId(store);
        if (cancelled) {
          return;
        }
        setDevice(code);
        setVault(opened);
        setSnapshot(await opened.loadSnapshot(now()));
        setEntries(await opened.entries());
      } catch (cause) {
        if (!cancelled) {
          setError(cause);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [store, tenantId, userId, now, deskAllowed]);

  // پاک‌سازی کامل با جابه‌جایی مستأجر (AC-IAM-002.3) و با خروج پس از تلاش همگام‌سازی (Snapshot_Encrypted_And_Purged_On_Logout).
  const vaultRef = useRef(vault);
  vaultRef.current = vault;
  const purge = useCallback(async () => {
    await OfflineVault.purge(store);
    setVault(null);
    setSnapshot(null);
    setEntries([]);
  }, [store]);
  useEffect(() => onReset(() => void purge()), [onReset, purge]);

  const transport = useCallback<SyncTransport>(
    (request: BilSyncOperationsRequest, idempotencyKey: string) =>
      unwrap(tenantApi.POST('/api/v1/sync/operations', { body: request, headers: { 'Idempotency-Key': idempotencyKey } })),
    [tenantApi],
  );

  const reload = useCallback(async (current: OfflineVault) => {
    setEntries(await current.entries());
  }, []);

  const syncNow = useCallback(async (): Promise<SyncSummary | null> => {
    const current = vaultRef.current;
    if (!current || !device) {
      return null;
    }
    syncingRef.current ??= (async () => {
      setSyncing(true);
      try {
        const summary = await syncOutbox(current, transport, device, now);
        setLastSync(summary);
        setError(null);
        setServerReachable(true);
        return summary;
      } catch (cause) {
        if (isApiError(cause) && cause.status === 0) {
          setServerReachable(false);
        }
        setError(cause);
        return null;
      } finally {
        await reload(current);
        setSyncing(false);
        syncingRef.current = null;
      }
    })();
    return syncingRef.current;
  }, [device, transport, now, reload]);

  useEffect(
    () =>
      auth.onBeforeLogout(async () => {
        if (status === 'online') {
          await syncNow();
        }
        await purge();
      }),
    [auth, status, syncNow, purge],
  );

  const refreshSnapshot = useCallback(async () => {
    const current = vaultRef.current;
    if (!current || !branchId || !deskAllowed) {
      return;
    }
    try {
      const fresh = await unwrap(tenantApi.GET('/api/v1/sync/snapshot', { params: { query: { branchId } } }));
      await current.saveSnapshot(fresh, now());
      setSnapshot(await current.loadSnapshot(now()));
      setServerReachable(true);
    } catch (cause) {
      if (isApiError(cause) && cause.status === 0) {
        setServerReachable(false);
      } else {
        setError(cause);
      }
    }
  }, [tenantApi, branchId, deskAllowed, now]);

  // رویدادهای اتصال مرورگر.
  useEffect(() => {
    const up = () => setBrowserOnline(true);
    const down = () => setBrowserOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);

  // Heartbeat هر ۱۰ ثانیه (ARD-offline §۲).
  useEffect(() => {
    if (!tenantId || !deskAllowed) {
      return undefined;
    }
    let stopped = false;
    const beat = async () => {
      setClock(now());
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        setServerReachable(false);
        return;
      }
      try {
        await unwrap(tenantApi.GET('/api/v1/sync/heartbeat'));
        if (!stopped) {
          setServerReachable(true);
        }
      } catch (cause) {
        if (!stopped) {
          // فقط خطای شبکه ← قطع؛ پاسخ خطای سرور یعنی سرور در دسترس است.
          setServerReachable(!(isApiError(cause) && cause.status === 0));
        }
      }
    };
    void beat();
    const timer = setInterval(() => void beat(), heartbeatMs);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [tenantApi, tenantId, deskAllowed, heartbeatMs, now]);

  // شروع و پایان قطع اتصال؛ اتصال دوباره ← همگام‌سازی خودکار و Snapshot تازه.
  const previous = useRef<ConnectionStatus>(status);
  useEffect(() => {
    if (status === 'offline') {
      setOfflineSince((value) => value ?? now());
    } else {
      setOfflineSince(null);
      if (previous.current === 'offline' || entries.some((entry) => entry.status === 'pending')) {
        void syncNow().then(() => refreshSnapshot());
      }
    }
    previous.current = status;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // Snapshot در شروع و هر ۱۵ دقیقه برخط (و با تغییر شعبه فعال).
  useEffect(() => {
    if (!vault || status !== 'online' || !deskAllowed) {
      return undefined;
    }
    void refreshSnapshot();
    const timer = setInterval(() => void refreshSnapshot(), SNAPSHOT_REFRESH_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vault, branchId, deskAllowed]);

  const maxHours = snapshot?.snapshot.maxOfflineHours ?? DEFAULT_MAX_OFFLINE_HOURS;
  const sessionExpired = offlineSince !== null && clock - offlineSince > Number(maxHours) * 3_600_000;

  const record = useCallback(
    async (build: () => Promise<Parameters<typeof enqueue>[1]>, display?: OutboxDisplay) => {
      const current = vaultRef.current;
      if (!current || !branchId) {
        throw new Error('offline_not_ready');
      }
      if (sessionExpired) {
        throw new Error('offline_session_expired');
      }
      const entry = await enqueue(current, await build(), display, now());
      await reload(current);
      if (status === 'online') {
        void syncNow();
      }
      return entry;
    },
    [branchId, sessionExpired, now, reload, status, syncNow],
  );

  const value = useMemo<OfflineContextValue>(
    () => ({
      status,
      offlineSince,
      sessionExpired,
      ready: vault !== null && device !== null,
      deviceId: device,
      snapshot,
      entries,
      pendingCount: entries.filter((entry) => entry.status === 'pending').length,
      conflictCount: entries.filter((entry) => entry.status === 'conflict').length,
      syncing,
      lastSync,
      error,
      refreshSnapshot,
      syncNow,
      checkIn: (appointmentId, display) => record(async () => checkInOperation(branchId ?? '', appointmentId, now()), display),
      register: (patient, display) => record(async () => registerOperation(branchId ?? '', patient, now()), display),
      checkout: async (input, display) => {
        const sequence = await nextSequence(store);
        return record(
          async () => checkoutOperation(branchId ?? '', { ...input, sequence }, now()),
          { ...display, provisionalNumber: device ? provisionalNumber(device, sequence) : undefined },
        );
      },
    }),
    [status, offlineSince, sessionExpired, vault, device, snapshot, entries, syncing, lastSync, error, refreshSnapshot, syncNow, record, branchId, now, store],
  );

  return <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>;
}
